import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, Button, CircularProgress, Link as MuiLink } from '@mui/material';

import { pbdqColors } from '../theme/pbdqTokens';
import { sdsColors } from '../theme/solutionsDesignSystem';
import pbdqRoutes from '../pbdqRoutes';

import PbdqChatMessage from './PbdqChatMessage.comp';
import PbdqChatUserMessage from './PbdqChatUserMessage.comp';

import {
    SelectOne, StepActions, ErrorList, CheckLine, Section, Message, ORANGE, MUTED, BORDER,
} from '../../DQPro/PbdqSignup/components/SignupFields.comp';
import {
    RegistryForm, emptyRegistry, DeploymentForm, DqEveryChildForm, ChecklistForm, DocumentsForm, NoteField, OrderReview,
} from '../../DQPro/PbdqSignup/components/SignupForms.comp';
import { TEXT, WEBSITE_URL, LEGAL_TERMS_URL } from '../../DQPro/PbdqSignup/signup.text';
import { saveToken, readToken, saveHandover } from '../../DQPro/PbdqSignup/signup.session';
import DqtCheckoutDialog from '../../DQPro/components/DqtCheckoutDialog.comp';
import {
    getSignupCatalogService,
    quoteSignupOrderService,
    startSignupApplicationService,
    getSignupApplicationService,
    saveSignupScreeningService,
    submitSignupChecklistService,
    uploadSignupDocumentService,
    submitSignupVerificationService,
    startSignupCardPaymentService,
    reviseSignupOrderService,
    startSignupWirePaymentService,
    uploadSignupWireProofService,
    getSignupPaymentStatusService,
    resendSignupActivationService,
} from '../../../services/pbdqSignup.service';

/**
 * PbdqAgentChatFlow — the DQ Agent conversation itself (catalog load, the vFF
 * guided step machine, quote/checkout/payment), extracted out of Chatbot.page.jsx
 * so it can also run inside PbdqFloatingChatWidget as an in-place overlay
 * instead of a full navigation to /powered-by-dq/chatbot.
 *
 * Chatbot.page.jsx is left untouched and keeps this exact same logic
 * duplicated (same pattern as CpaFloatingChatWidget / SignupFloatingChatWidget
 * being separate copies) because that route is a fixed destination: every
 * email link and card-payment return from my-dq-api points at
 * /powered-by-dq/chatbot directly, with ?ref=/?t=/?pay=1/?dqt_checkout=
 * params this in-page widget will never receive. When opened from any other
 * page (no such params present), both simply start the guided flow at CS1.
 */

const useStyles = makeStyles(() => ({
    scrollArea: {
        flex: '1 1 0%',
        minHeight: 0,
        padding: 20,
        display: 'grid',
        gap: 16,
        alignContent: 'start',
        overflowY: 'auto',
    },
    stepWrap: {
        display: 'grid',
        gap: 16,
        justifyItems: 'stretch',
    },
}));

// Same message-as-a-chat-turn shape as apply-v2's Bubble, restyled with this
// site's own bubble components.
const Bubble = ({ from = 'bot', children }) => (
    from === 'bot' ? <PbdqChatMessage>{children}</PbdqChatMessage> : <PbdqChatUserMessage>{children}</PbdqChatUserMessage>
);

const Paragraphs = ({ lines }) => lines.map((l) => <Typography key={l} sx={{ fontSize: '0.9rem', lineHeight: 1.6, mb: 1 }}>{l}</Typography>);

// Signup catalog solution code → this site's own solution page. Unlike apply-v2
// (an external funnel, where "Open the solution page" always points out to
// poweredbydq.com), this chat already lives on that site, so the same link
// should go straight to the matching page here instead of leaving it.
// '#RRGGBB' -> 'R, G, B', for CSS custom properties plugged into rgba(var(--x), a).
const hexToRgb = (hex) => {
    const n = parseInt(hex.replace('#', ''), 16);
    return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
};

const SOLUTION_ROUTES = {
    // COS Children and COS Government share the COS Assessments page (catalog
    // SOLUTION_PAGES: "Solutions › COS Assessments › What you can assess › …");
    // without P0 here, choosing Children left the page behind (Yufei-PbDQ-14Oct-01).
    P0: pbdqRoutes.cosAssessments,
    P0_GOV: pbdqRoutes.cosAssessments,
    P1: pbdqRoutes.schoolCosReadiness,
    LC: pbdqRoutes.learnCreator,
    P2A: pbdqRoutes.workforceAiReadiness,
    P2B: pbdqRoutes.workforceAiReadiness,
    P3: pbdqRoutes.programValidation,
    DQEVERYCHILD: pbdqRoutes.dqEveryChild,
};

// When it points at a page inside this site (internalRoute set), silently
// navigates the background page there itself — the chat reaching this step
// is the trigger, not a click, so no bubble is needed to confirm it. The
// floating widget lives above the route Switch (PbdqAppShell), so this
// navigation swaps only the page content underneath; the chat popup and its
// conversation stay mounted and open throughout. Only the external fallback
// (no internal route available) still renders a bubble, since that's the
// only way to reach it.
const PageLink = ({ solution, catalog, label = 'Open the solution page', internal = false, to }) => {
    const history = useHistory();
    const internalRoute = to || (internal ? SOLUTION_ROUTES[solution] : null);

    useEffect(() => {
        if (internalRoute) history.push(internalRoute);
        // Only the target route should re-trigger navigation, not identity changes of history.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [internalRoute]);

    if (internalRoute) return null;

    return (
        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '10px', border: `1px solid ${BORDER}`, background: pbdqColors.canvas }}>
            <Typography sx={{ fontSize: '0.8rem', color: MUTED }}>{catalog?.SOLUTION_PAGES?.[solution] || solution}</Typography>
            <MuiLink href={WEBSITE_URL} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 700, color: ORANGE, fontSize: '0.9rem' }}>{label} ↗</MuiLink>
        </Box>
    );
};

const PbdqAgentChatFlow = () => {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const [catalog, setCatalog] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [step, setStep] = useState('LOADING');
    const [stack, setStack] = useState([]);            // [{ step, bot, user }] — answered steps
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState([]);

    const [route, setRoute] = useState('');
    const [solution, setSolution] = useState('');
    const [waitlistName, setWaitlistName] = useState('');
    const [deployment, setDeployment] = useState({});
    const [dqec, setDqec] = useState({});
    const [registry, setRegistry] = useState(emptyRegistry());
    const [quote, setQuote] = useState(null);
    const [terms, setTerms] = useState(false);
    const [intendedUse, setIntendedUse] = useState('');
    const [checklist, setChecklist] = useState({});
    const [docNote, setDocNote] = useState('');
    const [dp5Choice, setDp5Choice] = useState('');

    const [app, setApp] = useState(null);              // { reference_no, token, ...publicView }
    const [payment, setPayment] = useState(null);       // { client_secret, item, wallets_enabled }
    const [paymentOpen, setPaymentOpen] = useState(false);
    const panelBodyRef = useRef(null);
    const latestTurnRef = useRef(null);

    const solutionOf = (value) => catalog?.SOLUTIONS?.find((s) => s.value === value);
    const solutionLabel = solutionOf(solution)?.label || waitlistName || solution;

    // ── Navigation ───────────────────────────────────────────────────────────
    const go = useCallback((next, { bot, user } = {}) => {
        setErrors([]);
        setStack((s) => [...s, { step, bot, user }]);
        setStep(next);
    }, [step]);

    const back = useCallback(() => {
        setErrors([]);
        setStack((s) => {
            if (!s.length) return s;
            setStep(s[s.length - 1].step);
            return s.slice(0, -1);
        });
    }, []);

    /*
     * The chat hands over to the DQ Pro V2 sign-up page (/signup), which asks for
     * an email and password and continues with the chat's own next steps.
     */
    const handOver = (handover) => {
        saveHandover({ ...handover, source_page: `${location.pathname}${location.search}` });
        history.push('/signup');
    };

    const restart = (to = 'CS1') => {
        setStack([]); setErrors([]); setApp(null); setQuote(null); setTerms(false); setSolution(''); setDeployment({}); setDqec({});
        setIntendedUse(''); setChecklist({}); setDocNote(''); setDp5Choice(''); setRegistry(emptyRegistry()); setPayment(null);
        setStep(to);
        if (location.search) history.replace({ pathname: location.pathname, search: to === 'DS1' ? '?route=direct' : '' });
    };

    // Scroll only the chat panel itself to the start of its latest turn.
    // Stopping at the turn's top (rather than scrollHeight) keeps a long agent
    // message readable from its first line; short turns still land at the end
    // because scrollTop is clamped to the maximum.
    useEffect(() => {
        const el = panelBodyRef.current;
        const turn = latestTurnRef.current;
        if (!el) return;
        const top = turn
            ? el.scrollTop + turn.getBoundingClientRect().top - el.getBoundingClientRect().top - 12
            : el.scrollHeight;
        el.scrollTo({ top, behavior: 'smooth' });
    }, [step]);

    // Retint the shared apply-v2 form/checkout pieces (SignupFields, SignupForms,
    // DqtCheckoutDialog) and the floating widget's own chrome (toggle bubble,
    // header icon — see CpaFloatingChatWidgetStyles.js) to match the rust-orange
    // sdsColors design system the main PBDQ pages (Home, Solutions, Capabilities,
    // Impact, Pricing) now use, instead of the older pbdqTokens orange. Set on
    // document.documentElement, not a page-root style, because the checkout
    // dialog renders through a MUI Dialog portal to document.body — a CSS custom
    // property set on this widget's own DOM subtree would never reach it. Cleaned
    // up on unmount so apply-v2 (which never sets these) is never affected.
    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty('--pbdq-accent', sdsColors.accent);
        root.style.setProperty('--pbdq-accent-hover', sdsColors.accentDark);
        root.style.setProperty('--pbdq-accent-tint', sdsColors.tint);
        root.style.setProperty('--pbdq-accent-rgb', hexToRgb(sdsColors.accent));
        return () => {
            root.style.removeProperty('--pbdq-accent');
            root.style.removeProperty('--pbdq-accent-hover');
            root.style.removeProperty('--pbdq-accent-tint');
            root.style.removeProperty('--pbdq-accent-rgb');
        };
    }, []);

    const fail = (res, fallback = 'Something went wrong. Please try again.') => {
        const list = res?.errors || res?.data?.errors;
        setErrors(Array.isArray(list) && list.length ? list : [res?.message || fallback]);
    };

    // ── Entry ────────────────────────────────────────────────────────────────
    const openFromLink = useCallback(async (ref, token, next) => {
        const res = await getSignupApplicationService(ref, token).catch(() => null);
        const view = res?.data?.application;
        if (!view) { setStep('LINK_INVALID'); return; }
        saveToken(ref, token);
        setApp({ ...view, token });
        setSolution(view.solution);
        setRoute(view.route);
        next(view);
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await getSignupCatalogService();
                if (cancelled) return;
                setCatalog(res?.data || null);
            } catch (_) {
                if (!cancelled) { setLoadError(true); return; }
            }

            const ref = params.get('ref');
            const linkToken = params.get('t') || (ref ? readToken(ref) : null);
            const sessionId = params.get('dqt_checkout');
            if (ref && sessionId) {
                const token = readToken(ref);
                if (!token) { setStep('LINK_INVALID'); return; }
                const res = await getSignupPaymentStatusService(ref, token, sessionId).catch(() => null);
                const view = res?.data?.application;
                if (!view) { setStep('LINK_INVALID'); return; }
                setApp({ ...view, token }); setSolution(view.solution); setRoute(view.route);
                setStep(['paid', 'fulfilled'].includes(view.status) ? 'PAID' : 'PAYMENT_PROCESSING');
                return;
            }
            if (ref && linkToken && params.get('pay') === '1') {
                await openFromLink(ref, linkToken, (view) => setStep(view.status === 'awaiting_payment' ? 'PAY' : (['paid', 'fulfilled'].includes(view.status) ? 'PAID' : 'LINK_INVALID')));
                return;
            }
            if (ref && linkToken && params.get('upload') === '1') {
                await openFromLink(ref, linkToken, (view) => setStep(view.type === 'dq_pro_applicant' && view.status === 'submitted' ? 'UPLOAD_EVIDENCE' : 'LINK_INVALID'));
                return;
            }
            const waitlist = params.get('waitlist');
            if (waitlist) { setRoute('waitlist'); setWaitlistName(waitlist.slice(0, 120)); setStep('WE1'); return; }
            if (params.get('route') === 'direct') { setRoute('direct'); setStep('DS1'); return; }
            setStep('CS1');
        })();
        return () => { cancelled = true; };
        // Entry is decided once, from the URL the visitor arrived with.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Server actions ───────────────────────────────────────────────────────
    const startApplication = async (type, extra = {}) => {
        setBusy(true);
        try {
            const res = await startSignupApplicationService({
                type, solution, route, registry: { ...registry, confirmations: { ...registry.confirmations, terms } },
                source_page: `${location.pathname}${location.search}`, ...extra,
            });
            const d = res?.data;
            if (!d?.reference_no) { fail(res); return null; }
            saveToken(d.reference_no, d.access_token);
            const next = { ...d.application, token: d.access_token };
            setApp(next);
            return next;
        } catch (e) {
            fail(e?.response?.data || e);
            return null;
        } finally {
            setBusy(false);
        }
    };

    const checkDeployment = async () => {
        setBusy(true);
        try {
            const res = await quoteSignupOrderService(solution, deployment);
            if (!res?.data) { fail(res); return false; }
            setQuote(res.data);
            return true;
        } catch (e) {
            fail(e?.response?.data || e);
            return false;
        } finally {
            setBusy(false);
        }
    };

    const addDocument = async (fields, file) => {
        setBusy(true);
        try {
            const res = await uploadSignupDocumentService(app.reference_no, app.token, fields, file);
            if (!res?.data?.application) { fail(res); return false; }
            setApp((a) => ({ ...a, ...res.data.application }));
            return true;
        } catch (e) {
            fail(e?.response?.data || e);
            return false;
        } finally {
            setBusy(false);
        }
    };

    /*
     * What the saved order was made from. Go back stays open on the order page after
     * Proceed to Payment, so anything changed since is sent as a revision of the same
     * order rather than left behind or started as a second one.
     */
    const savedOrderRef = useRef('');
    const orderKey = () => JSON.stringify({ solution, deployment, registry, terms });

    const openPayment = async () => {
        setBusy(true);
        try {
            let current = app;
            if (current && current.type === 'direct_order' && savedOrderRef.current !== orderKey()) {
                const res = await reviseSignupOrderService(current.reference_no, current.token, {
                    solution, deployment, registry: { ...registry, confirmations: { ...registry.confirmations, terms } },
                });
                const view = res?.data?.application;
                if (!view) { fail(res); return; }
                current = { ...current, ...view };
                setApp(current);
                savedOrderRef.current = orderKey();
            }
            if (!current) {
                current = await startApplication('direct_order', { deployment });
                if (!current) return;
                savedOrderRef.current = orderKey();
            }
            const res = await startSignupCardPaymentService(current.reference_no, current.token);
            if (!res?.data?.client_secret) { fail(res); return; }
            setPayment(res.data);
            setPaymentOpen(true);
        } catch (e) {
            fail(e?.response?.data || e);
        } finally {
            setBusy(false);
        }
    };

    const afterCardPaid = async (sessionId) => {
        setPaymentOpen(false);
        setStep('PAYMENT_PROCESSING');
        const res = await getSignupPaymentStatusService(app.reference_no, app.token, sessionId).catch(() => null);
        const view = res?.data?.application;
        if (view) setApp((a) => ({ ...a, ...view }));
        setStep(view && ['paid', 'fulfilled'].includes(view.status) ? 'PAID' : 'PAYMENT_PROCESSING');
    };

    const resend = async () => {
        setBusy(true);
        try { await resendSignupActivationService(app.reference_no, app.token); } catch (_) { /* invokeApi shows the reason */ } finally { setBusy(false); }
    };

    const firstName = app?.contact?.first_name || registry.contact.first_name;
    const orgName = app?.organization?.name || registry.organization.name;
    const email = app?.contact?.email || registry.contact.email;

    // ── Steps ────────────────────────────────────────────────────────────────
    // The vFF step tree — step ids match the document's sections.
    const renderStep = () => {
        if (!catalog) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: ORANGE }}/></Box>;
        switch (step) {
            case 'CS1':
                return (
                    <>
                        <Bubble><Message value={TEXT.CS1.message}/></Bubble>
                        <SelectOne options={TEXT.CS1.options} value={route} onChange={setRoute}/>
                        <StepActions nextDisabled={!route} onNext={() => go(route === 'direct' ? 'DS1' : 'DP1', {
                            bot: TEXT.CS1.message, user: TEXT.CS1.options.find((o) => o.value === route)?.label,
                        })}/>
                    </>
                );

            // ─── Direct ───────────────────────────────────────────────────────
            case 'DS1':
                return (
                    <>
                        <Bubble><Message value={TEXT.DS1}/></Bubble>
                        <SelectOne options={catalog.SOLUTIONS} value={solution} onChange={(v) => { setSolution(v); setDeployment({}); setQuote(null); }}/>
                        <StepActions onBack={stack.length ? back : undefined} nextDisabled={!solution}
                            onNext={() => go('DS2', { bot: TEXT.DS1, user: solutionLabel })}/>
                    </>
                );
            case 'DS2':
                return (
                    <>
                        <Bubble><Message value={TEXT.REVIEW}/><PageLink solution={solution} catalog={catalog} internal/></Bubble>
                        <StepActions onBack={back}
                            extra={<Button onClick={back} sx={{ textTransform: 'none', color: ORANGE, fontWeight: 700 }}>{TEXT.DIFFERENT_SOLUTION}</Button>}
                            onNext={() => go(solution === 'P0_GOV' ? 'GOV_NOTICE' : solution === 'DQEVERYCHILD' ? 'DQEC' : 'DS3', { bot: TEXT.REVIEW, user: 'Continue' })}/>
                    </>
                );
            case 'DS3':
                return (
                    <>
                        <Bubble><Message value={solution === 'P3' ? TEXT.DS3_6 : TEXT.DS3}/></Bubble>
                        <DeploymentForm catalog={catalog} solution={solution} value={deployment} onChange={setDeployment}/>
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} onNext={async () => {
                            // Hand-over (13 Oct 2026): the rest of a direct order — information,
                            // order review, payment — continues on the sign-up page.
                            if (await checkDeployment()) handOver({ route: 'direct', solution, deployment });
                        }}/>
                    </>
                );
            case 'DQEC':
                return (
                    <>
                        <Bubble><Message value={TEXT.DS3}/></Bubble>
                        <DqEveryChildForm catalog={catalog} value={dqec} onChange={setDqec}/>
                        <StepActions onBack={back} onNext={() => go('DS4', { bot: TEXT.DS3, user: '#DQEveryChild details entered' })}/>
                    </>
                );
            case 'GOV_NOTICE':
                return (
                    <>
                        <Bubble>
                            <Typography sx={{ fontWeight: 800, mb: 1 }}>{TEXT.GOV_NOTICE.title}</Typography>
                            <Paragraphs lines={TEXT.GOV_NOTICE.paragraphs}/>
                            <ul style={{ marginTop: 0 }}>{TEXT.GOV_NOTICE.list.map((l) => <li key={l}>{l}</li>)}</ul>
                            <Paragraphs lines={[TEXT.GOV_NOTICE.after, TEXT.GOV_NOTICE.evidenceTitle]}/>
                            <ul style={{ marginTop: 0, marginBottom: 0 }}>{TEXT.GOV_NOTICE.evidence.map((l) => <li key={l}>{l}</li>)}</ul>
                        </Bubble>
                        <StepActions onBack={back} onNext={() => go(route === 'dq_pro' ? 'DP6' : 'DS4', { bot: TEXT.GOV_NOTICE.title, user: 'Continue' })}/>
                    </>
                );
            case 'DS4':
                return (
                    <>
                        <Bubble><Message value={TEXT.DS4}/></Bubble>
                        <RegistryForm catalog={catalog} value={registry} onChange={setRegistry}/>
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} onNext={async () => {
                            if (solution === 'P0_GOV') {
                                const created = app || await startApplication('gov_verification');
                                if (created) go('DOCS', { bot: TEXT.DS4, user: `${registry.contact.first_name} ${registry.contact.last_name}, ${registry.organization.name}` });
                                return;
                            }
                            go(solution === 'DQEVERYCHILD' ? 'DQEC_REVIEW' : 'DS5', { bot: TEXT.DS4, user: `${registry.contact.first_name} ${registry.contact.last_name}, ${registry.organization.name}` });
                        }}/>
                    </>
                );
            case 'DOCS':
                return (
                    <>
                        <Bubble><Message value={TEXT.DOCUMENTS(firstName)}/></Bubble>
                        <DocumentsForm catalog={catalog} withType documents={app?.documents || []} onAdd={addDocument} busy={busy}/>
                        <NoteField label={TEXT.DOCUMENT_NOTE} value={docNote} onChange={setDocNote}/>
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} nextLabel={route === 'dq_pro' ? 'Continue' : 'Submit request'}
                            nextDisabled={!(app?.documents || []).length}
                            onNext={async () => {
                                setBusy(true);
                                try {
                                    const res = await submitSignupVerificationService(app.reference_no, app.token, docNote);
                                    if (!res?.data) { fail(res); return; }
                                    setApp((a) => ({ ...a, ...res.data.application }));
                                    go('GOV_DONE', { bot: TEXT.DOCUMENTS(firstName), user: `${(app.documents || []).length} document(s) submitted` });
                                } catch (e) { fail(e?.response?.data || e); } finally { setBusy(false); }
                            }}/>
                    </>
                );
            case 'GOV_DONE':
                return (
                    <>
                        <Bubble>
                            <Typography sx={{ fontWeight: 800, mb: 1 }}>{TEXT.GOV_SUBMITTED.title}</Typography>
                            <Paragraphs lines={TEXT.GOV_SUBMITTED.body(firstName, orgName, email)}/>
                        </Bubble>
                        <StepActions nextLabel="View other solutions" onNext={() => restart(route === 'dq_pro' ? 'DP1' : 'DS1')}
                            backLabel="Close" onBack={() => history.push('/powered-by-dq')}/>
                    </>
                );
            case 'DS5':
                return (
                    <>
                        <Bubble><Message value={TEXT.DS5}/></Bubble>
                        {quote && <OrderReview quote={quote} organization={registry.organization.name} organizationType={registry.organization.type} solutionLabel={solutionLabel} deployment={deployment} catalog={catalog}/>}
                        <Section>
                            <CheckLine checked={terms} onChange={setTerms}>
                                I agree to the Powered by DQ <a href={LEGAL_TERMS_URL} target="_blank" rel="noopener noreferrer" style={{ color: ORANGE }}>Terms of Service</a>.*
                            </CheckLine>
                        </Section>
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} nextDisabled={!terms || !quote || !(quote.total_usd > 0)}
                            nextLabel="Proceed to Payment" onNext={openPayment}/>
                    </>
                );
            case 'DQEC_REVIEW':
                return (
                    <>
                        <Bubble><Message value={TEXT.DS5_8}/></Bubble>
                        <Section title="Your request">
                            <Box sx={{ fontSize: '0.88rem', lineHeight: 1.8 }}>
                                <div>Organization: <strong>{registry.organization.name}</strong></div>
                                <div>Solution: <strong>#DQEveryChild</strong></div>
                                <div>Components: <strong>{(dqec.components || []).map((c) => catalog.DQEC_COMPONENTS.find((o) => o.value === c)?.label).join(', ')}</strong></div>
                                <div>Expected number of children: <strong>{dqec.expected_children}</strong></div>
                                <div>Expected number of schools or participating organizations: <strong>{dqec.expected_schools}</strong></div>
                            </Box>
                        </Section>
                        <Section title="What happens next"><Typography sx={{ fontSize: '0.88rem' }}>{TEXT.DS5_8_NEXT(registry.contact.email)}</Typography></Section>
                        <Section>
                            <CheckLine checked={terms} onChange={setTerms}>
                                I agree to the Powered by DQ <a href={LEGAL_TERMS_URL} target="_blank" rel="noopener noreferrer" style={{ color: ORANGE }}>Terms of Service</a>.*
                            </CheckLine>
                        </Section>
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} nextDisabled={!terms} nextLabel="Submit" onNext={async () => {
                            const created = await startApplication('dqeverychild', { deployment: dqec });
                            if (created) go('DQEC_DONE', { bot: TEXT.DS5_8, user: 'Request submitted' });
                        }}/>
                    </>
                );
            case 'DQEC_DONE':
                return (
                    <>
                        <Bubble>
                            <Typography sx={{ fontWeight: 800, mb: 1 }}>Your request has been received.</Typography>
                            <Typography sx={{ fontSize: '0.9rem' }}>{TEXT.DS5_8_NEXT(email)}</Typography>
                        </Bubble>
                        <StepActions nextLabel="View other solutions" onNext={() => restart('DS1')}/>
                    </>
                );

            // ─── Payment (DS-6 / DP-10) ───────────────────────────────────────
            case 'PAY':
                return (
                    <>
                        <Bubble>
                            {app?.type === 'dq_pro_applicant'
                                ? `Welcome back, ${firstName}. Your DQ Pro partnership for ${solutionLabel} is ready for payment.`
                                : <Message value={TEXT.DS5}/>}
                        </Bubble>
                        {app?.quote && <OrderReview quote={app.quote} organization={orgName} solutionLabel={solutionLabel} deployment={app.deployment} catalog={catalog}/>}
                        <ErrorList errors={errors}/>
                        <StepActions busy={busy} nextLabel="Proceed to Payment" onNext={openPayment}/>
                    </>
                );
            case 'PAYMENT_PROCESSING':
                return (
                    <Bubble>
                        Your payment is being confirmed. We’ll email {email || 'you'} as soon as your workspace is ready.
                        {app && (
                            <Box sx={{ mt: 1.5 }}>
                                <Button size="small" onClick={async () => {
                                    const res = await getSignupApplicationService(app.reference_no, app.token).catch(() => null);
                                    const view = res?.data?.application;
                                    if (view) { setApp((a) => ({ ...a, ...view })); if (['paid', 'fulfilled'].includes(view.status)) setStep('PAID'); }
                                }} sx={{ textTransform: 'none', color: ORANGE, fontWeight: 700 }}>Check again</Button>
                            </Box>
                        )}
                    </Bubble>
                );
            case 'PAID': {
                const partner = app?.type === 'dq_pro_applicant';
                const t = app?.workspace_ready ? (partner ? TEXT.DP11 : TEXT.DS7) : (partner ? TEXT.DP11_PENDING : TEXT.DS7_PENDING);
                return (
                    <>
                        <Bubble>
                            <Typography sx={{ fontWeight: 800, mb: 1 }}>{t.title(firstName)}</Typography>
                            <Paragraphs lines={t.body(solutionLabel, email)}/>
                        </Bubble>
                        <StepActions backLabel="Close" onBack={() => history.push('/powered-by-dq')} busy={busy}
                            nextLabel="Resend Activation Email" nextDisabled={!app?.workspace_ready} onNext={resend}/>
                    </>
                );
            }
            case 'WIRE_DONE':
                return (
                    <>
                        <Bubble>{TEXT.WIRE_SUBMITTED(email)}</Bubble>
                        <StepActions backLabel="Close" onBack={() => history.push('/powered-by-dq')}/>
                    </>
                );

            // ─── DQ Pro ───────────────────────────────────────────────────────
            case 'DP1':
                return (
                    <>
                        <Bubble><Message value={TEXT.DP1}/></Bubble>
                        <SelectOne options={catalog.SOLUTIONS.filter((s) => s.dq_pro)} value={solution} onChange={setSolution}/>
                        <StepActions onBack={stack.length ? back : undefined} nextDisabled={!solution}
                            onNext={() => go('DP2', { bot: TEXT.DP1, user: solutionLabel })}/>
                    </>
                );
            case 'DP2':
                return (
                    <>
                        <Bubble><Message value={TEXT.REVIEW}/><PageLink solution={solution} catalog={catalog} internal/></Bubble>
                        <StepActions onBack={back}
                            extra={<Button onClick={back} sx={{ textTransform: 'none', color: ORANGE, fontWeight: 700 }}>{TEXT.DIFFERENT_SOLUTION}</Button>}
                            onNext={() => go('DP3', { bot: TEXT.REVIEW, user: 'Continue' })}/>
                    </>
                );
            case 'DP3':
                return (
                    <>
                        <Bubble><Message value={TEXT.DP3}/><PageLink solution={solution} catalog={{ SOLUTION_PAGES: { [solution]: 'Partners › Your DQ Pro Workspace' } }} label="Open the partners page" to={`${pbdqRoutes.pricing}#workspace`}/></Bubble>
                        <StepActions onBack={back} onNext={() => go('DP4', { bot: TEXT.DP3, user: 'Continue' })}/>
                    </>
                );
            case 'DP4':
                return (
                    <>
                        <Bubble><Message value={TEXT.DP4}/><PageLink solution={solution} catalog={{ SOLUTION_PAGES: { [solution]: 'Partners › DQ Pro opportunity calculator' } }} label="Open the calculator" to={`${pbdqRoutes.pricing}#calculator`}/></Bubble>
                        <StepActions onBack={back} onNext={() => go('DP5', { bot: TEXT.DP4, user: 'Continue' })}/>
                    </>
                );
            case 'DP5':
                return (
                    <>
                        <Bubble><Message value={TEXT.DP5(solutionLabel)}/></Bubble>
                        <SelectOne options={[{ value: 'yes', label: 'Yes, let’s continue' }, { value: 'different', label: TEXT.DIFFERENT_SOLUTION }]}
                            value={dp5Choice} onChange={setDp5Choice}/>
                        <StepActions onBack={back} nextDisabled={!dp5Choice} onNext={() => {
                            if (dp5Choice === 'different') { restart('DP1'); setRoute('dq_pro'); return; }
                            // Hand-over (13 Oct 2026): everything after DP-5 — for COS Government
                            // too — continues on the sign-up page.
                            handOver({ route: 'dq_pro', solution });
                        }}/>
                    </>
                );
            case 'DP6': {
                const govPath = solution === 'P0_GOV';
                return (
                    <>
                        <Bubble>
                            <Message value={TEXT.DP6}/>
                            {!govPath && <Typography sx={{ fontSize: '0.85rem', color: MUTED, mt: 1 }}>{TEXT.DP6_1_NOTE}</Typography>}
                        </Bubble>
                        <RegistryForm catalog={catalog} value={registry} onChange={setRegistry} legalEntity/>
                        {!govPath && <Typography sx={{ mt: 2, fontSize: '0.85rem', color: MUTED }}>{TEXT.DP6_1_NEXT}</Typography>}
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} onNext={async () => {
                            if (app) { go(solution === 'P0_GOV' ? 'DOCS' : 'DP7', { bot: TEXT.DP6, user: registry.organization.name }); return; }
                            const created = await startApplication(solution === 'P0_GOV' ? 'gov_verification' : 'dq_pro_applicant');
                            if (created) go(solution === 'P0_GOV' ? 'DOCS' : 'DP7', { bot: TEXT.DP6, user: registry.organization.name });
                        }}/>
                    </>
                );
            }
            case 'DP7':
                return (
                    <>
                        <Bubble><Message value={TEXT.DP7.message}/></Bubble>
                        <SelectOne label={TEXT.DP7.label} help={TEXT.DP7.help} options={catalog.INTENDED_USE} value={intendedUse} onChange={setIntendedUse}/>
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} nextDisabled={!intendedUse} onNext={() => {
                            const answer = catalog.INTENDED_USE.find((o) => o.value === intendedUse)?.label;
                            go(intendedUse === 'own' ? 'DP7_1' : 'DP8', { bot: TEXT.DP7.message, user: answer });
                        }}/>
                    </>
                );
            case 'DP7_1':
                return (
                    <>
                        <Bubble>
                            <Typography sx={{ fontWeight: 800, mb: 1 }}>{TEXT.DP7_1.title}</Typography>
                            <Paragraphs lines={TEXT.DP7_1.body}/>
                        </Bubble>
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} nextLabel="Confirm" onNext={async () => {
                            setBusy(true);
                            try {
                                const res = await saveSignupScreeningService(app.reference_no, app.token, 'own');
                                if (!res?.data) { fail(res); return; }
                                restart('DS1');
                                setRoute('direct');
                            } catch (e) { fail(e?.response?.data || e); } finally { setBusy(false); }
                        }}/>
                    </>
                );
            case 'DP8':
                return (
                    <>
                        <Bubble><Message value={TEXT.DP8.message}/></Bubble>
                        <ChecklistForm catalog={catalog} value={checklist} onChange={setChecklist}/>
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} nextLabel="Submit" onNext={async () => {
                            setBusy(true);
                            try {
                                const s1 = await saveSignupScreeningService(app.reference_no, app.token, intendedUse);
                                if (!s1?.data) { fail(s1); return; }
                                const res = await submitSignupChecklistService(app.reference_no, app.token, checklist);
                                if (!res?.data) { fail(res); return; }
                                setApp((a) => ({ ...a, ...res.data.application }));
                                go('DP9', { bot: TEXT.DP8.message, user: 'Checklist submitted' });
                            } catch (e) { fail(e?.response?.data || e); } finally { setBusy(false); }
                        }}/>
                    </>
                );
            case 'DP9':
                return (
                    <>
                        <Bubble>
                            <Typography sx={{ fontWeight: 800, mb: 1 }}>{TEXT.DP9.title}</Typography>
                            <Paragraphs lines={TEXT.DP9.body(orgName, email)}/>
                        </Bubble>
                        <StepActions nextLabel="View other solutions" onNext={() => { restart('DP1'); setRoute('dq_pro'); }}
                            backLabel="Close" onBack={() => history.push('/powered-by-dq')}/>
                    </>
                );
            case 'UPLOAD_EVIDENCE':
                return (
                    <>
                        <Bubble>
                            <Typography sx={{ fontWeight: 800, mb: 1 }}>{TEXT.DP9_1_UPLOAD.title}</Typography>
                            <Typography sx={{ fontSize: '0.85rem', mb: 1 }}>Reference: {app?.reference_no} — {orgName}</Typography>
                            <ul style={{ marginTop: 0, marginBottom: 0 }}>{TEXT.DP9_1_UPLOAD.body.map((l) => <li key={l} style={{ fontSize: '0.88rem' }}>{l}</li>)}</ul>
                        </Bubble>
                        <DocumentsForm catalog={catalog} withType={false} documents={app?.documents || []} onAdd={addDocument} busy={busy}/>
                        <ErrorList errors={errors}/>
                        {(app?.documents || []).length > 0 && (
                            <Typography sx={{ mt: 2, fontSize: '0.88rem', color: MUTED }}>
                                Thank you. We’ll begin the review with your documents and contact you at {email} with the outcome.
                            </Typography>
                        )}
                    </>
                );

            // ─── Waitlist ─────────────────────────────────────────────────────
            case 'WE1':
                return (
                    <>
                        <Bubble><Message value={TEXT.WE1(waitlistName)}/></Bubble>
                        <StepActions backLabel="Close" onBack={() => history.push('/powered-by-dq')} onNext={() => go('WE2', { bot: TEXT.WE1(waitlistName)[0], user: 'Continue' })}/>
                    </>
                );
            case 'WE2':
                return (
                    <>
                        <Bubble><Message value={TEXT.WE2}/></Bubble>
                        <RegistryForm catalog={catalog} value={registry} onChange={setRegistry} confirmations={false}/>
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} onNext={async () => {
                            const created = await startApplication('waitlist', { solution_name: waitlistName, solution: '' });
                            if (created) go('WE3', { bot: TEXT.WE2, user: registry.organization.name });
                        }}/>
                    </>
                );
            case 'WE3':
                return (
                    <>
                        <Bubble>
                            <Typography sx={{ fontWeight: 800, mb: 1 }}>{TEXT.WE3.title}</Typography>
                            <Paragraphs lines={TEXT.WE3.body(firstName, orgName, waitlistName, email)}/>
                        </Bubble>
                        <StepActions backLabel="Close" onBack={() => history.push('/powered-by-dq')} onNext={() => restart('CS1')}/>
                    </>
                );

            case 'LINK_INVALID':
                return (
                    <>
                        <Bubble>This link has expired or is not valid on this device. Please use the latest email from DQ, or start again.</Bubble>
                        <StepActions nextLabel="Start again" onNext={() => restart('CS1')}/>
                    </>
                );
            default:
                return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: ORANGE }}/></Box>;
        }
    };

    const directReceive = (() => {
        const q = app?.quote || quote;
        if (!q || app?.type === 'dq_pro_applicant') return null;
        const credits = (q.credits_included || 0) + (q.credits_purchased || 0);
        return [q.licences ? `${q.licences.toLocaleString('en-US')} licenses` : null, credits ? `${credits.toLocaleString('en-US')} credits` : null].filter(Boolean).join(' and ') || null;
    })();

    return (
        <>
            <div className={classes.scrollArea} ref={panelBodyRef}>
                {loadError ? (
                    <Bubble>We couldn’t load the sign-up right now. Please refresh the page to try again.</Bubble>
                ) : (
                    <>
                        {stack.map((entry, i) => (
                            <div key={i} className={classes.stepWrap}>
                                {entry.bot && <Bubble><Message value={entry.bot}/></Bubble>}
                                {entry.user && <Bubble from="user">{entry.user}</Bubble>}
                            </div>
                        ))}
                        <div className={classes.stepWrap} ref={latestTurnRef}>
                            {renderStep()}
                        </div>
                    </>
                )}
            </div>

            {app && payment && (
                <DqtCheckoutDialog
                    open={paymentOpen}
                    publishableKey={catalog?.stripe_publishable_key}
                    clientSecret={payment.client_secret}
                    item={payment.item}
                    walletsEnabled={payment.wallets_enabled}
                    paymentOptions={catalog?.payment_options}
                    receiveLabel={app.type === 'dq_pro_applicant' ? 'DQ Tokens' : 'licenses and credits'}
                    receiveText={directReceive}
                    fallbackEmail={email}
                    onClose={() => setPaymentOpen(false)}
                    onPaid={afterCardPaid}
                    createWire={() => startSignupWirePaymentService(app.reference_no, app.token)}
                    uploadProof={(_orderId, file, note) => uploadSignupWireProofService(app.reference_no, app.token, file, note)}
                    onWireSubmitted={() => { setPaymentOpen(false); setStep('WIRE_DONE'); }}
                />
            )}
        </>
    );
};

export default PbdqAgentChatFlow;
